import React from 'react';
import { Button, Card, CardContent, CardMedia, Typography, Box, Checkbox } from '@mui/material';
import { styled } from '@mui/material/styles';

const ProductCard = styled(Card)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  },
}));

const ProductImage = styled(CardMedia)({
  height: 230,
  objectFit: 'contain',
  backgroundColor: '#f5f5f5',
});

const ProductTitle = styled(Typography)({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minHeight: '3.6em', // 2 lines of text
  marginBottom: '8px',
});

const PriceText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: 'bold',
  fontSize: '1.2rem',
  margin: '8px 0',
}));

const getSiteFromUrl = (url) => {
  try {
    const domain = new URL(url).hostname;
    if (domain.includes('tmall')) return 'Tmall';
    if (domain.includes('1688')) return '1688';
    return 'Taobao';
  } catch {
    return 'Taobao';
  }
};


const TaobaoProductCard = ({ product, onImport, disabled = false, selectable = false, selected = false, onSelect }) => {
  const numericPrice = Number(product.price);
  const sourceUsd = product.pricing?.sourceUsd != null
    ? Number(product.pricing.sourceUsd)
    : (Number.isFinite(numericPrice) ? numericPrice * 0.14 : null);
  const displayTitle = product.englishTitle || product.multiLanguageInfo?.title || product.title;
  return (
    <ProductCard sx={{ position: 'relative', outline: selected ? '2px solid #4338ca' : 'none' }}>
      {selectable && (
        <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: '4px' }}>
          <Checkbox
            size="small"
            checked={selected}
            onChange={(e) => onSelect && onSelect(product, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
      )}
      <Box sx={{ cursor: selectable ? 'pointer' : 'default' }} onClick={() => selectable && onSelect && onSelect(product, !selected)}>
      <ProductImage
        component="img"
        image={product.pic_url || product.picUrl || product.mainImageUrl || 'https://via.placeholder.com/300'}
        title={displayTitle}
        alt={displayTitle}
      />
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <ProductTitle variant="subtitle1" component="h3">
          {displayTitle}
        </ProductTitle>

        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1 }}>
          Source: {getSiteFromUrl(product.detail_url || '')}
        </Typography>

        {(product.shopName || product.shop?.nick) && (
          <Typography variant="body2" color="text.secondary">
            Seller: {product.shopName || product.shop?.nick}
          </Typography>
        )}

        {product.sales > 0 && (
          <Typography variant="body2" color="text.secondary">
            Sales: {product.sales.toLocaleString()}
          </Typography>
        )}

        <Box sx={{ mt: 'auto' }}>
          <PriceText>
            ¥{product.price || 'N/A'}
            {sourceUsd != null && (
              <Typography component="span" sx={{ ml: 1, color: 'text.primary', fontSize: '0.9rem', fontWeight: 600 }}>
                ≈ ${sourceUsd.toFixed(2)} USD
              </Typography>
            )}
            {product.original_price && product.original_price !== product.price && (
              <Typography 
                component="span" 
                sx={{ 
                  textDecoration: 'line-through', 
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  ml: 1
                }}
              >
                ¥{product.original_price}
              </Typography>
            )}
          </PriceText>
          {product.pricing?.available && (
            <Box sx={{ mb: 1, p: 1.25, borderRadius: 1.5, bgcolor: product.pricing.diffPercent <= 0 ? '#ecfdf3' : '#fff1f2', border: '1px solid', borderColor: product.pricing.diffPercent <= 0 ? '#86efac' : '#fda4af' }}>
              <Typography variant="caption" display="block" sx={{ lineHeight: 1.5 }}>
                Target retail (2× landed): <b>${Number(product.pricing.estimatedSellUsd || 0).toFixed(2)}</b><br />
                Google Shopping average: <b>${Number(product.pricing.averagePrice || 0).toFixed(2)}</b>
              </Typography>
              <Typography variant="caption" fontWeight={700} color={product.pricing.diffPercent <= 0 ? 'success.main' : 'error.main'}>
                {product.pricing.diffPercent <= 0 ? 'Competitive ' : 'Above market '}
                {product.pricing.diffPercent > 0 ? '+' : ''}{product.pricing.diffPercent}%
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              size="small"
              color="primary"
              variant="outlined"
              href={product.detail_url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              View
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={disabled}
              onClick={(e) => { e.stopPropagation(); onImport(product); }}
              sx={{
                '&.Mui-disabled': {
                  pointerEvents: 'auto',
                  cursor: 'not-allowed',
                  opacity: 0.7
                }
              }}
            >
              Import
            </Button>
          </Box>
        </Box>
      </CardContent>
      </Box>
    </ProductCard>
  );
};

export default TaobaoProductCard;
